import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { surveysApi } from '@/services/api/surveys'
import type {
  CreateSurveyInput,
  UpdateSurveyInput,
  PublishSurveyInput,
} from '@/services/api/surveys'

export function useSurveys() {
  return useQuery({
    queryKey: ['admin', 'surveys'],
    queryFn: surveysApi.list,
    staleTime: 60_000,
  })
}

export function useSurvey(id: string) {
  return useQuery({
    queryKey: ['admin', 'surveys', id],
    queryFn: () => surveysApi.get(id),
    enabled: !!id,
  })
}

export function useSurveyResults(id: string) {
  return useQuery({
    queryKey: ['admin', 'surveys', id, 'results'],
    queryFn: () => surveysApi.getResults(id),
    enabled: !!id,
  })
}

export function useCreateSurvey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateSurveyInput) => surveysApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'surveys'] })
    },
  })
}

export function useUpdateSurvey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSurveyInput }) =>
      surveysApi.update(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'surveys'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'surveys', variables.id] })
    },
  })
}

export function useDeleteSurvey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => surveysApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'surveys'] })
    },
  })
}

export function usePublishSurvey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PublishSurveyInput }) =>
      surveysApi.publish(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'surveys'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'surveys', variables.id] })
    },
  })
}
